import React, { FC, useState } from 'react'
import AddIcon from 'transfix-icons/build/AddIcon'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import EllipsisIcon from 'transfix-icons/build/EllipsisIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Box from 'transfix-ui/core/Box'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { calcRem } from 'transfix-ui/theme/typography'
import { ShipperStrikePrices } from 'apps/marketplace/apiTypes/shipperStrikePrices'
import { TableRow, TableCell, OverflowMenu } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import {
  formatAsPercentage,
  formatOverridePercentage,
  formatEquipmentInfo,
  formatStrikePrice,
  formatOverrideDateRange,
} from '../utils/shipperStrikePrice'

interface IStrikePriceTableRowProps {
  shipperStrikePrice: ShipperStrikePrices
  addOrEditOverride: (price: ShipperStrikePrices) => void
  deleteOverride: (price: ShipperStrikePrices) => void
}

const useStyles = makeStyles(theme => ({
  tableCell: {
    backgroundColor: brandColors.coolGray1,
    textAlign: 'right',
  },
  equipmentTypeLabel: {
    fontSize: calcRem(12),
    color: brandColors.coolGray5,
    fontWeight: 700,
    marginBottom: theme.spacing(0.2),
  },
  equipmentType: {
    fontWeight: 700,
  },
  defaultIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: theme.spacing(2),
    fontSize: calcRem(10),
    fontWeight: 700,
    borderRadius: '2px',
    // Colors need the !important flag here because
    // otherwise they will be overwritten by the Tooltip
    // component CSS:
    backgroundColor: `${brandColors.coolGray4} !important`,
    color: `${brandColors.coolGray8} !important`,
    cursor: 'default',
  },
  shipperIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: theme.spacing(2),
    fontSize: calcRem(10),
    fontWeight: 700,
    borderRadius: '2px',
    // Colors need the !important flag here because
    // otherwise they will be overwritten by the Tooltip
    // component CSS:
    backgroundColor: `${brandColors.skyBlue6} !important`,
    color: `${brandColors.white} !important`,
    cursor: 'default',
  },
}))

export const StrikePriceTableRow: FC<IStrikePriceTableRowProps> = ({
  addOrEditOverride,
  deleteOverride,
  shipperStrikePrice,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const {
    costAssumption,
    id,
    isDefault,
    manualOverride,
    marginPercentage,
    strikePrice,
    winPercentage,
  } = shipperStrikePrice
  const hasOverride = manualOverride !== null
  const classes = useStyles()
  const overflowOptions = [
    {
      itemComponent: (
        <Box display='flex' alignItems='center'>
          <EditIcon size='large' />
          <Box ml={1}>Edit</Box>
        </Box>
      ),
      label: 'edit',
      action: () => addOrEditOverride(shipperStrikePrice),
    },
    {
      itemComponent: (
        <Box display='flex' alignItems='center'>
          <DeleteIcon size='large' />
          <Box ml={1}>Delete</Box>
        </Box>
      ),
      label: 'delete',
      action: () => deleteOverride(shipperStrikePrice),
    },
  ]
  const defaultDetails =
    "This is the default setting because Transfix doesn't have enough data yet."
  const shipperDetails =
    'Transfix has enough shipment data to calculate a shipper-specific TCM strategy for this combination of haul type and equipment type.'
  return (
    <TableRow>
      <TableCell>
        <Box display='flex' alignItems='center'>
          {isDefault ? (
            <Tooltip position='top' trigger={<Box className={classes.defaultIcon}>D</Box>}>
              <Box width='250px' display='flex' alignItems='center'>
                <Box className={classes.defaultIcon}>D</Box>
                <Box flexGrow={1}>{defaultDetails}</Box>
              </Box>
            </Tooltip>
          ) : (
            <Tooltip position='top' trigger={<Box className={classes.shipperIcon}>T</Box>}>
              <Box width='220px' display='flex' alignItems='center'>
                <Box className={classes.shipperIcon}>T</Box>
                <Box flexGrow={1}>{shipperDetails}</Box>
              </Box>
            </Tooltip>
          )}
          <Box flexGrow={1} display='flex' flexDirection='column'>
            <Box className={classes.equipmentTypeLabel}>Bid % to DAT</Box>
            <Box className={classes.equipmentType}>{formatEquipmentInfo(shipperStrikePrice)}</Box>
          </Box>
        </Box>
      </TableCell>
      <TableCell className={classes.tableCell}>
        {costAssumption === null ? DISPLAY.empty : formatAsPercentage(costAssumption)}
      </TableCell>
      <TableCell className={classes.tableCell}>{formatStrikePrice(strikePrice)}</TableCell>
      <TableCell className={classes.tableCell}>
        {marginPercentage === null ? DISPLAY.empty : formatAsPercentage(marginPercentage)}
      </TableCell>
      <TableCell className={classes.tableCell}>
        {winPercentage === null ? DISPLAY.empty : formatAsPercentage(winPercentage)}
      </TableCell>
      <TableCell>
        {hasOverride ? (
          formatOverridePercentage(manualOverride?.overridePercentage)
        ) : (
          <IconButton
            Icon={AddIcon}
            label='Add'
            color='tertiary'
            onClick={() => addOrEditOverride(shipperStrikePrice)}
            size='large'
          />
        )}
      </TableCell>
      <TableCell>{hasOverride ? formatOverrideDateRange(manualOverride) : ''}</TableCell>
      <TableCell>{manualOverride?.reason || ''}</TableCell>
      <TableCell>
        {hasOverride ? (
          <OverflowMenu
            onExpanded={setIsExpanded}
            expanded={isExpanded}
            dropdownId={`${id}-overflow`}
            trigger={
              <IconButton
                Icon={EllipsisIcon}
                label='edit'
                data-test='edit-row'
                onClick={() => setIsExpanded(true)}
                size='large'
              />
            }
            options={overflowOptions}
          />
        ) : (
          ''
        )}
      </TableCell>
    </TableRow>
  )
}
